import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Alert,
  LinearProgress,
} from "@mui/material";
import { getMenusreadyToServe } from "../Services/MenuApi";
import { toast } from "react-toastify";
import { useUser } from "../Auth/useUser";

const EditInventoryModal = ({
  open,
  onClose,
  onSave,
  organisationId,
  inventory, // The inventory to edit (always required for this modal)
  t,
}) => {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inventoryDate, setInventoryDate] = useState("");
  const [inventoryNotes, setInventoryNotes] = useState("");
  const [inventoryFilter, setInventoryFilter] = useState("");

  const user = useUser();
  const infoUser = user.result;

  useEffect(() => {
    if (open && organisationId && inventory) {
      // Populate header fields from the existing inventory
      setInventoryDate(
        inventory.date
          ? inventory.date.split("T")[0]
          : new Date().toISOString().split("T")[0],
      );
      setInventoryNotes(inventory.notes || "");
      setInventoryFilter("");
      loadMenus();
    }
  }, [open, organisationId, inventory]);

  const loadMenus = async () => {
    try {
      setLoading(true);
      const response = await getMenusreadyToServe(organisationId);

      if (response.data.success) {
        const targetEmplacement = inventory.emplacement || "Central";

        const menusWithInventory = response.data.data.map((menu) => {
          let currentStock = 0;
          let inventoryCount = 0;
          let difference = 0;
          let notes = "";

          // Determine current system stock
          if (targetEmplacement === "main_stock") {
            currentStock = menu.main_stock?.quantite_stock || 0;
          } else {
            const specificStock = menu.all_stocks?.find(
              (s) => s.emplacement === targetEmplacement,
            );
            currentStock = specificStock?.quantite_stock || 0;
          }

          // Override with the saved inventory values so they appear in the fields
          if (inventory.items) {
            const savedItem = inventory.items.find(
              (i) => i.produit_id._id === menu._id,
            );

            if (savedItem) {
              currentStock = savedItem.current_stock;
              inventoryCount = savedItem.inventory_count;
              difference = savedItem.difference;
              notes = savedItem.notes || "";
            } else {
              inventoryCount = "";
            }
          } else {
            inventoryCount = "";
          }

          return {
            ...menu,
            currentStock,
            inventoryCount,
            difference,
            notes,
          };
        });

        setMenus(menusWithInventory);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (index, field, value) => {
    setMenus((prev) => {
      const updated = [...prev];
      if (field === "inventoryCount") {
        if (value === "") {
          // User cleared the input — remove from inventory by resetting to initial state
          updated[index].inventoryCount = "";
          updated[index].difference = 0;
          updated[index]._removedFromInventory = true;
        } else {
          const parsed = parseFloat(value) || 0;
          updated[index].inventoryCount = parsed;
          updated[index].difference = parsed - updated[index].currentStock;
          updated[index]._removedFromInventory = false;
        }
      } else {
        updated[index][field] = value;
      }
      return updated;
    });
  };

  const getFilteredMenus = () => {
    if (!inventoryFilter) return menus;
    return menus.filter((menu) => {
      const title = menu.titre?.fr || menu.titre || "";
      const ref = menu.reference || "";
      return (
        title.toLowerCase().includes(inventoryFilter.toLowerCase()) ||
        ref.toLowerCase().includes(inventoryFilter.toLowerCase())
      );
    });
  };

  const getSummary = () => {
    const itemsWithChanges = menus.filter((item) => item.difference !== 0);
    const totalDifference = menus.reduce(
      (sum, item) => sum + item.difference,
      0,
    );
    return {
      totalItems: menus.length,
      itemsWithChanges: itemsWithChanges.length,
      totalDifference,
      positiveDifferences: menus.filter((item) => item.difference > 0).length,
      negativeDifferences: menus.filter((item) => item.difference < 0).length,
    };
  };

  const getStatusChip = (difference) => {
    if (difference > 0)
      return { color: "success", icon: "fa-arrow-up", label: t("Excédent") };
    if (difference < 0)
      return { color: "error", icon: "fa-arrow-down", label: t("Manquant") };
    return { icon: "fa-check", label: t("OK") };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const itemsToUpdate = menus.filter((item) => item.difference !== 0);
      if (itemsToUpdate.length === 0) {
        toast.info("Aucune modification à sauvegarder");
        return;
      }

      const inventoryData = {
        _id: inventory._id,
        organisation_id: organisationId,
        date: inventoryDate,
        notes: inventoryNotes,
        emplacement: inventory.emplacement || "main_stock",
        caisse_id: inventory.caisse_id || null,
        created_by: infoUser.id,
        items: menus
          .filter((menu) => {
            const wasAlreadySaved = inventory.items?.some(
              (i) => i.produit_id._id === menu._id,
            );
            const hasNewCount = menu.inventoryCount !== "";
            const wasRemoved = menu._removedFromInventory === true;
            return (wasAlreadySaved || hasNewCount) && !wasRemoved;
          })
          .map((menu) => ({
            produit_id: menu._id,
            current_stock: menu.currentStock,
            inventory_count:
              menu.inventoryCount === "" ? 0 : menu.inventoryCount,
            difference: menu.difference,
            notes: menu.notes,
          })),
        adjustments: itemsToUpdate.map((item) => ({
          produit_id: item._id,
          old_quantity: item.currentStock,
          new_quantity: item.inventoryCount,
          difference: item.difference,
          notes: item.notes || "",
        })),
      };

      const success = await onSave(inventoryData);
      if (success) onClose();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const filteredMenus = getFilteredMenus();
  const summary = getSummary();

  const summaryItems = [
    {
      label: t("Produits"),
      value: summary.totalItems,
      icon: "fa-box",
      color: "text.primary",
    },
    {
      label: t("Avec changements"),
      value: summary.itemsWithChanges,
      icon: "fa-arrows-rotate",
      color: "info.main",
    },
    {
      label: t("Augmentations"),
      value: summary.positiveDifferences,
      icon: "fa-arrow-trend-up",
      color: "success.main",
    },
    {
      label: t("Diminutions"),
      value: summary.negativeDifferences,
      icon: "fa-arrow-trend-down",
      color: "error.main",
    },
    {
      label: t("Différence totale"),
      value: summary.totalDifference,
      icon: "fa-scale-balanced",
      color:
        summary.totalDifference > 0
          ? "success.main"
          : summary.totalDifference < 0
            ? "error.main"
            : "text.secondary",
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ bgcolor: "warning.main", color: "#fff" }}>
        <Typography variant="h6">
          <i className="fa-solid fa-pen-to-square" style={{ marginRight: 8 }} />
          {t("Modifier l'inventaire")}
          {inventory?.code && (
            <Typography
              component="span"
              variant="body2"
              sx={{ ml: 2, opacity: 0.85 }}
            >
              — {inventory.code}
            </Typography>
          )}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
          disabled={saving}
        >
          <i className="fa-solid fa-xmark" />
        </IconButton>
      </DialogTitle>

      {loading && <LinearProgress />}

      <DialogContent dividers>
        {/* <Alert severity="warning" sx={{ mb: 2 }}>
          {t(
            "Vous modifiez un inventaire existant. Les quantités saisies remplaceront les valeurs précédentes."
          )}
        </Alert> */}

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <TextField
              label={t("Date de l'inventaire")}
              type="date"
              fullWidth
              value={inventoryDate}
              onChange={(e) => setInventoryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("Rechercher un produit")}
              placeholder={t("Nom, référence...")}
              fullWidth
              value={inventoryFilter}
              onChange={(e) => setInventoryFilter(e.target.value)}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("Notes")}
              fullWidth
              value={inventoryNotes}
              onChange={(e) => setInventoryNotes(e.target.value)}
              disabled={saving}
              multiline
              rows={1}
            />
          </Grid>
        </Grid>

        {/* Emplacement read-only badge */}
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={
              <i
                className="fa-solid fa-location-dot"
                style={{ fontSize: 13 }}
              />
            }
            label={`${t("Emplacement")} : ${inventory?.emplacement || t("Stock Principal")}`}
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Summary cards */}
        <Grid
          container
          spacing={2}
          sx={{
            padding: "5px 0px",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
            mb: 2,
          }}
        >
          {summaryItems.map((item, index) => (
            <Grid item key={index}>
              <Paper
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className={`fa-solid ${item.icon}`}
                    style={{ fontSize: 20 }}
                  />
                </Box>
                <Box>
                  <Typography variant="h5" color={item.color}>
                    {index === 4 && item.value > 0 ? "+" : ""}
                    {item.value}
                  </Typography>
                  <Typography color="text.secondary">{item.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          {t("Produits")} ({filteredMenus.length})
        </Typography>

        {filteredMenus.length === 0 ? (
          <Alert severity="info">
            {t("Aucun produit trouvé. Vérifiez vos filtres.")}
          </Alert>
        ) : (
          <Paper sx={{ maxHeight: 400, overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="25%">{t("Produit")}</TableCell>
                  <TableCell align="center" width="15%">
                    {t("Stock Actuel")}
                  </TableCell>
                  <TableCell width="20%">{t("Inventaire Physique")}</TableCell>
                  <TableCell align="center" width="15%">
                    {t("Différence")}
                  </TableCell>
                  <TableCell width="15%">{t("Notes")}</TableCell>
                  <TableCell width="10%">{t("Statut")}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredMenus.map((menu, index) => {
                  // Find the real index in the unfiltered array for updates
                  const realIndex = menus.findIndex((m) => m._id === menu._id);
                  const status = getStatusChip(menu.difference);
                  return (
                    <TableRow
                      key={menu._id}
                      sx={{
                        bgcolor:
                          menu.difference !== 0
                            ? menu.difference > 0
                              ? "rgba(76, 175, 80, 0.08)"
                              : "rgba(244, 67, 54, 0.08)"
                            : "inherit",
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight="bold">
                          {menu.titre?.fr || menu.titre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {menu.reference || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2">
                          {menu.currentStock}
                        </Typography>
                      </TableCell>

                      {/* ✅ Controlled input — value is bound so saved data shows up */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={menu.inventoryCount}
                          placeholder="-"
                          onChange={(e) =>
                            handleUpdate(
                              realIndex,
                              "inventoryCount",
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          disabled={saving}
                          error={menu.inventoryCount < 0}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          fontWeight="bold"
                          color={
                            menu.difference > 0
                              ? "success.main"
                              : menu.difference < 0
                                ? "error.main"
                                : "text.secondary"
                          }
                        >
                          {menu.difference > 0 ? "+" : ""}
                          {menu.difference}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={menu.notes}
                          placeholder={t("Notes...")}
                          onChange={(e) =>
                            handleUpdate(realIndex, "notes", e.target.value)
                          }
                          disabled={saving}
                          multiline
                          rows={1}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          color={status.color}
                          label={
                            <>
                              <i
                                className={`fa-solid ${status.icon}`}
                                style={{ marginRight: 6 }}
                              />
                              {status.label}
                            </>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button
          onClick={onClose}
          disabled={saving}
          startIcon={<i className="fa-solid fa-xmark" />}
        >
          {t("Annuler")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="warning"
          onClick={handleSave}
          disabled={saving || summary.itemsWithChanges === 0}
          startIcon={<i className="fa-solid fa-floppy-disk" />}
        >
          {saving ? t("Enregistrement...") : t("Mettre à jour l'inventaire")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInventoryModal;
